import { XMarkIcon } from '@heroicons/react/24/solid';
import { useState, useEffect } from 'react';

interface HiddenExampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { rule: string; reasoning: string; comment: string }) => void;
  example?: any;
}

export const HiddenExampleModal = ({
  isOpen,
  onClose,
  onSave,
  example,
}: HiddenExampleModalProps) => {
  const [rule, setRule] = useState('');
  const [comment, setComment] = useState('');
  const [reasoning, setReasoning] = useState('');

  const MAX_LENGTH = 250;

  useEffect(() => {
    if (example) {
      setRule(example.rule || '');
      setComment(example.comment || '');
      setReasoning(example.reasoning || '');
    } else {
      setRule('');
      setComment('');
      setReasoning('');
    }
  }, [example, isOpen]);

  const handleSave = () => {
    if (rule.trim() && comment.trim() && reasoning.trim()) {
      onSave({
        rule: rule.trim(),
        reasoning: reasoning.trim(),
        comment: comment.trim(),
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const isRuleOverLimit = rule.length > MAX_LENGTH;
  const isCommentOverLimit = comment.length > MAX_LENGTH;
  const isReasoningOverLimit = reasoning.length > MAX_LENGTH;
  const isFormValid =
    rule.trim() &&
    comment.trim() &&
    reasoning.trim() &&
    !isRuleOverLimit &&
    !isCommentOverLimit &&
    !isReasoningOverLimit;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
    >
      <div className="bg-grey rounded-xl p-6 w-full max-w-2xl mx-4 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {example
              ? 'Edit Comment Moderation Example'
              : 'Add Comment Moderation Example'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Rule input first */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-200">
                Rule
              </label>
              <span
                className={`text-xs ${
                  isRuleOverLimit
                    ? 'text-red-400'
                    : rule.length > MAX_LENGTH * 0.9
                      ? 'text-yellow-400'
                      : 'text-gray-400'
                }`}
              >
                {rule.length}/{MAX_LENGTH}
              </span>
            </div>
            <input
              type="text"
              className={`bg-black border rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 ${
                isRuleOverLimit
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-600 focus:ring-violet'
              }`}
              placeholder="e.g. Hide comments that negatively criticizes shipping times"
              value={rule}
              onChange={(e) => setRule(e.target.value)}
              maxLength={MAX_LENGTH + 50}
            />
            {isRuleOverLimit && (
              <p className="text-red-400 text-xs mt-1">
                Rule is too long. Please keep it under {MAX_LENGTH} characters.
              </p>
            )}
          </div>

          {/* Reasoning input second */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-200">
                Reasoning
              </label>
              <span
                className={`text-xs ${
                  isReasoningOverLimit
                    ? 'text-red-400'
                    : reasoning.length > MAX_LENGTH * 0.9
                      ? 'text-yellow-400'
                      : 'text-gray-400'
                }`}
              >
                {reasoning.length}/{MAX_LENGTH}
              </span>
            </div>
            <textarea
              className={`bg-black border rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 h-24 ${
                isReasoningOverLimit
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-600 focus:ring-violet'
              }`}
              placeholder="e.g. Negative shipping comments may discourage potential customers and harm conversion rates"
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              maxLength={MAX_LENGTH + 50}
            />
            {isReasoningOverLimit && (
              <p className="text-red-400 text-xs mt-1">
                Reasoning is too long. Please keep it under {MAX_LENGTH}{' '}
                characters.
              </p>
            )}
          </div>

          {/* Comment Example input third */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-200">
                Comment Example
              </label>
              <span
                className={`text-xs ${
                  isCommentOverLimit
                    ? 'text-red-400'
                    : comment.length > MAX_LENGTH * 0.9
                      ? 'text-yellow-400'
                      : 'text-gray-400'
                }`}
              >
                {comment.length}/{MAX_LENGTH}
              </span>
            </div>
            <input
              type="text"
              className={`bg-black border rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 ${
                isCommentOverLimit
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-600 focus:ring-violet'
              }`}
              placeholder="e.g. Didn't receive my order"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={MAX_LENGTH + 50}
            />
            {isCommentOverLimit && (
              <p className="text-red-400 text-xs mt-1">
                Comment example is too long. Please keep it under {MAX_LENGTH}{' '}
                characters.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isFormValid}
            className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
              !isFormValid
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-yellow text-black hover:brightness-90'
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
